import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AttendanceState, StampKind } from '@wf/shared'
import { api } from '../api/client'
import { noteServerNow } from '../offline/serverClock'
import { autoStopRunningTimer } from '../time/autoStop'
import { submitStamp } from './submitStamp'

// Letzter bekannter Anwesenheitsstand, gespiegelt in localStorage: erlaubt sofortigen Render beim Boot und
// einen plausiblen Stand offline. Server bleibt die Wahrheit (Query überschreibt beim nächsten Erfolg/Focus).
const CACHE_KEY = 'wf.attendance.v1'

export function loadCachedAttendance(): AttendanceState | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return undefined
    const v = JSON.parse(raw) as AttendanceState
    return v && typeof v.status === 'string' && typeof v.version === 'number' ? v : undefined
  } catch {
    return undefined
  }
}

export function saveCachedAttendance(state: AttendanceState): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(state))
  } catch {
    /* Persistenz nicht verfügbar — kein Absturz; der Stand kommt ohnehin per Query nach. */
  }
}

/** Eigener Anwesenheitsstand. Kein Polling — Aktualität trägt refetch-on-focus (queryClient-Default). */
export function useAttendanceMe() {
  return useQuery({
    queryKey: ['attendance', 'me'],
    queryFn: async () => {
      const state = await api.attendanceMe()
      noteServerNow(state.serverNow) // Uhren-Offset am Server-Zeitpunkt eichen (Boot/Focus) — vor dem ersten Tippen
      return state
    },
    placeholderData: () => loadCachedAttendance(),
  })
}

/** Anwesenheit des ganzen Teams heute (für das „Wer ist da?"-Panel der Annahme). Kein Polling. */
export function useAttendanceToday() {
  return useQuery({ queryKey: ['attendance', 'today'], queryFn: api.attendanceToday })
}

/** Folgestatus eines Stempels (für die optimistische Offline-Fortschreibung). */
function statusAfter(kind: StampKind): AttendanceState['status'] {
  if (kind === 'kommen' || kind === 'pause_ende') return 'an'
  if (kind === 'pause_start') return 'pause'
  return 'aus'
}

export function useStamp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { kind: StampKind; version: number }) => submitStamp(vars.kind, vars.version),
    onMutate: (vars) => {
      // A3 Stempel-Kopplung: gehen/Pause bei laufendem Auftrags-Timer → Timer JETZT stoppen & bis zum
      // Stempel-Moment buchen (sonst liefe Auftragszeit im Feierabend weiter = genau die Divergenz des Gates).
      if (vars.kind === 'gehen' || vars.kind === 'pause_start') {
        const stopped = autoStopRunningTimer()
        if (stopped) {
          void stopped.delivered.then((r) => {
            if (r === 'sent') void qc.invalidateQueries({ queryKey: ['orderTime', stopped.orderId] })
          })
        }
      }
    },
    onSuccess: (outcome, vars) => {
      if (outcome.result === 'sent') {
        qc.setQueryData<AttendanceState>(['attendance', 'me'], outcome.state)
        // Der neue Stempel steckt auch in Report/Timesheet/Wochen-Chart des Zeiten-Tabs — sonst zeigt
        // „Heute gestempelt" nach dem Tippen weiter den alten Stand (Review-Fund Zeitsystem).
        void qc.invalidateQueries({ queryKey: ['zeiten'] })
        return
      }
      if (outcome.result === 'converted') {
        // In einen Auto-Korrekturantrag umgewandelt: der Anwesenheitsstand bleibt (kein Stempel), aber die
        // Korrekturliste (['zeiten','corrections'] als Präfix) hat einen Eintrag mehr → Zeiten-Tab auffrischen.
        qc.setQueryData<AttendanceState>(['attendance', 'me'], outcome.state)
        void qc.invalidateQueries({ queryKey: ['zeiten'] })
        return
      }
      // Offline gequeued → optimistisch fortschreiben; die Server-Wahrheit folgt beim Flush/Focus.
      qc.setQueryData<AttendanceState>(['attendance', 'me'], (prev) => {
        const base: AttendanceState = prev ?? { status: 'aus', version: vars.version, todaySeconds: 0, liveSince: null }
        const status = statusAfter(vars.kind)
        return { ...base, status, version: base.version + 1, liveSince: status === 'an' ? Date.now() : null }
      })
    },
    // 409 (Versions-/Zustandskonflikt) o. Ä. → frischen Stand laden (exakt, ohne andere Queries zu clobbern).
    onError: () => void qc.invalidateQueries({ queryKey: ['attendance', 'me'], exact: true }),
  })
}
